import styled, { keyframes, css } from "styled-components";

/* ═══════════════════════════════════════════
   Hero Section
   ═══════════════════════════════════════════ */

export const HeroSection = styled.section`
  position: relative;
  background: var(--navy);
  padding: 3.5rem 2rem 3rem;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 50%;
    height: 100%;
    background: linear-gradient(135deg, transparent 40%, rgba(228,32,44,0.08) 100%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    height: 60px;
    background: var(--bg-page);
    clip-path: polygon(0 100%, 100% 100%, 100% 0, 0 100%);
  }
`;

export const HeroContent = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

export const HeroEyebrow = styled.div`
  font-family: var(--font-display);
  font-size: 0.85rem;
  color: var(--red);
  letter-spacing: 0.2em;
  text-transform: uppercase;
  font-weight: 700;
  margin-bottom: 0.8rem;
  display: flex;
  align-items: center;
  gap: 10px;

  &::before {
    content: '';
    width: 24px;
    height: 3px;
    background: var(--red);
    border-radius: 2px;
  }
`;

export const HeroTitle = styled.h1`
  font-family: var(--font-display);
  font-weight: 800;
  font-size: clamp(2.2rem, 4.5vw, 3.5rem);
  letter-spacing: -0.02em;
  line-height: 1.08;
  margin: 0 0 1rem;
  color: #fff;
  text-transform: uppercase;

  span {
    color: var(--red);
  }
`;

export const HeroSubtitle = styled.p`
  font-size: 1.05rem;
  color: rgba(255, 255, 255, 0.6);
  max-width: 500px;
  line-height: 1.6;
  margin: 0 0 2rem;
  font-weight: 400;
`;

export const StatsRow = styled.div`
  display: flex;
  gap: 2.5rem;
  flex-wrap: wrap;
`;

export const StatItem = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

export const StatNumber = styled.span`
  font-family: var(--font-display);
  font-size: 2rem;
  font-weight: 800;
  color: var(--red);
`;

export const StatLabel = styled.span`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
`;

/* ═══════════════════════════════════════════
   Controls Section
   ═══════════════════════════════════════════ */

export const ControlsSection = styled.section`
  max-width: 1280px;
  margin: 0 auto;
  padding: 2rem 2rem 1.5rem;

  @media (max-width: 768px) {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
`;

export const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--card-bg);
  border: 1.5px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 5px 5px 5px 20px;
  margin-bottom: 1.5rem;
  transition: var(--transition);
  box-shadow: var(--shadow-sm);

  &:focus-within {
    border-color: var(--navy);
    box-shadow: 0 0 0 3px rgba(26,34,82,0.1);
  }

  svg {
    width: 20px;
    height: 20px;
    color: var(--gray-400);
    flex-shrink: 0;
  }
`;

export const SearchInput = styled.input`
  flex: 1;
  background: none;
  border: none;
  color: var(--navy);
  font-family: inherit;
  font-size: 0.95rem;
  outline: none;
  font-weight: 500;

  &::placeholder {
    color: var(--gray-400);
  }
`;

export const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 1.2rem;
  flex-wrap: wrap;
`;

export const FilterLabel = styled.span`
  font-size: 0.75rem;
  color: var(--gray-500);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-right: 4px;
`;

export const ViewControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 12px;
`;

export const ResultsCount = styled.div`
  font-size: 0.9rem;
  color: var(--gray-500);
  font-weight: 500;

  strong {
    color: var(--navy);
    font-weight: 800;
  }
`;

export const SortSelect = styled.select`
  background: var(--card-bg);
  color: var(--gray-600);
  border: 1.5px solid var(--gray-200);
  border-radius: var(--radius-sm);
  padding: 8px 14px;
  font-family: inherit;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  outline: none;
`;

/* ═══════════════════════════════════════════
   Tournament Grid
   ═══════════════════════════════════════════ */

export const TournamentGridContainer = styled.section`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 2rem 4rem;

  @media (max-width: 768px) {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
`;

export const TournamentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

/* ═══════════════════════════════════════════
   Tournament Card
   ═══════════════════════════════════════════ */

const cardAppear = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const TournamentCard = styled.article<{ delay?: number }>`
  display: flex;
  flex-direction: column;
  background: var(--card-bg);
  border: 1.5px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  transition: var(--transition);
  -webkit-tap-highlight-color: transparent;
  animation: ${cardAppear} 0.5s ease backwards;
  animation-delay: ${({ delay = 0 }) => delay}ms;
  box-shadow: var(--shadow-sm);

  &:hover {
    transform: translateY(-5px);
    border-color: var(--border-hover);
    box-shadow: var(--shadow-lg);
  }
`;

export const CardBanner = styled.div`
  position: relative;
  height: 350px;
  overflow: hidden;
  background: var(--navy);

  img {
    width: 100%;
    height: 100%;
    object-fit: fill;
    display: block;
    transition: transform 0.5s ease;
  }

  ${TournamentCard}:hover & img {
    transform: scale(1.04);
  }
`;

export const CardTopBar = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2px 10px;
  background: var(--card-bg);
  gap: 8px;
  overflow: hidden;
`;

export const CardTopBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

export const CardBannerOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(26,34,82,0.3) 0%,
    rgba(26,34,82,0.08) 40%,
    transparent 100%
  );
`;

export const CardBadges = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const DateBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  background: #fff;
  border-radius: var(--radius-md);
  padding: 6px 12px;
  box-shadow: var(--shadow-md);
  line-height: 1.1;
  z-index: 1;
`;

export const DateDay = styled.span`
  font-family: var(--font-display);
  font-size: 1.25rem;
  font-weight: 900;
  color: var(--red-600, #c41820);
`;

export const DateMonth = styled.span`
  font-family: var(--font-display);
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--navy-700, #243070);
  text-transform: uppercase;
  letter-spacing: 0.15em;
`;

export const DateLabel = styled.span`
  font-family: var(--font-body);
  font-size: 0.55rem;
  font-weight: 600;
  color: var(--gray-400, #9ca3af);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  line-height: 1;
`;

export const ShareBubble = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(34, 197, 94, 0.12);
  border: none;
  border-radius: 50%;
  color: var(--navy, #1a2252);
  font-size: 1rem;
  cursor: pointer;
  z-index: 2;
  transition: background 0.2s, transform 0.2s;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: rgba(34, 197, 94, 0.22);
    transform: scale(1.1);
  }
`;

export const CardBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px 18px 18px;
`;

export const CardTitle = styled.h3`
  font-family: var(--font-display);
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 8px;
  letter-spacing: 0.01em;
  line-height: 1.25;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-transform: uppercase;
  color: var(--navy);
`;

export const CardDescription = styled.div`
  font-family: var(--font-body);
  font-size: 0.82rem;
  line-height: 1.6;
  color: var(--gray-600);
  margin-bottom: 14px;

  b, strong {
    color: var(--navy);
    font-weight: 700;
  }

  [style*="color: red"], [style*="color:red"] {
    color: var(--red) !important;
  }
`;

/* ── Price Section ── */
export const PriceSection = styled.div`
  background: var(--gray-50);
  border-radius: var(--radius-sm);
  padding: 10px 12px;
  margin-bottom: 12px;
  border: 1px solid var(--gray-100);
`;

export const PriceTier = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;

  &:not(:last-child) {
    border-bottom: 1px solid var(--gray-200);
    padding-bottom: 6px;
    margin-bottom: 4px;
  }
`;

export const PriceTierName = styled.span`
  font-size: 0.78rem;
  color: var(--gray-500);
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
`;

export const PriceTierDot = styled.span<{ variant?: "active" | "warn" | "neutral" }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ variant }) =>
    variant === "active" ? "#0ea572" :
    variant === "warn" ? "#f59e0b" :
    "var(--gray-300)"
  };
`;

export const PriceTierRight = styled.div`
  text-align: right;
`;

export const PriceTierValue = styled.span<{ promo?: boolean; free?: boolean }>`
  font-family: var(--font-display);
  font-size: ${({ free }) => free ? "1rem" : "0.95rem"};
  font-weight: 800;
  color: ${({ promo, free }) => (promo || free) ? "#0ea572" : "var(--navy)"};
`;

export const PriceTierDeadline = styled.span`
  font-size: 0.68rem;
  color: var(--gray-400);
  display: block;
  font-weight: 500;
`;

/* ── Warning / Info tags ── */
export const CardWarning = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fef3f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 12px;
  font-size: 0.78rem;
  color: var(--red);
  font-weight: 600;
`;

export const CardInfoTag = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 12px;
  font-size: 0.78rem;
  color: #065f46;
  font-weight: 600;
`;

/* ── Capacity Section ── */
export const CapacitySection = styled.div`
  margin-bottom: 14px;
`;

/* ── Card Bottom (capacity + footer agrupados no final) ── */
export const CardBottom = styled.div`
  margin-top: auto;
`;

/* ── Card Btn Row (Share + OSS acima do Footer) ── */
export const CardBtnRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 8px;
`;

/* ── Card Footer ── */
export const CardFooter = styled.div`
  display: flex;
  gap: 8px;

  a {
    flex: 1;
    justify-content: center;
    padding: 10px 16px;
    font-size: 0.82rem;
  }
`;

/* ═══════════════════════════════════════════
   Card First Tier (preview no card compacto)
   ═══════════════════════════════════════════ */

export const CardFirstTier = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--gray-50);
  border: 1px solid var(--gray-100);
  border-radius: var(--radius-sm);
  padding: 8px 12px;
  margin-bottom: 12px;

  .label {
    font-size: 0.78rem;
    color: var(--gray-500);
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 6px;

    &::before {
      content: '';
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #0ea572;
    }
  }

  .value {
    font-family: var(--font-display);
    font-size: 0.95rem;
    font-weight: 800;
    color: #0ea572;
  }
`;

/* ═══════════════════════════════════════════
   Detail Panel (slide-in lateral)
   ═══════════════════════════════════════════ */

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

export const DetailOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 1000;
  animation: ${fadeIn} 0.25s ease;
`;

export const DetailPanel = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  width: 540px;
  height: 100vh;
  background: var(--card-bg, #fff);
  z-index: 1001;
  display: flex;
  flex-direction: column;
  animation: ${slideIn} 0.3s var(--ease-out, cubic-bezier(0.16, 1, 0.3, 1));
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.15);
  overflow: hidden;

  @media (max-width: 600px) {
    width: 100%;
  }
`;

export const DetailHero = styled.div`
  position: relative;
  height: 240px;
  overflow: hidden;
  background: var(--navy);
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

export const DetailHeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(26, 34, 82, 0.5) 0%,
    rgba(26, 34, 82, 0.15) 50%,
    transparent 100%
  );
`;

export const DetailBack = styled.button`
  position: absolute;
  top: 16px;
  left: 16px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  color: #fff;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background 0.2s;
  z-index: 2;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

export const DetailBadges = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

export const DetailBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;

  @media (max-width: 600px) {
    padding: 16px;
  }
`;

export const DetailTitle = styled.h2`
  font-family: var(--font-display);
  font-size: 1.35rem;
  font-weight: 800;
  color: var(--navy);
  text-transform: uppercase;
  letter-spacing: 0.01em;
  line-height: 1.2;
  margin: 0 0 20px;
`;

export const DetailSection = styled.section`
  margin-bottom: 20px;
`;

export const DetailSectionTitle = styled.h4`
  font-family: var(--font-display);
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--gray-400);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin: 0 0 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--gray-100);
`;

export const DetailDescription = styled.div`
  font-family: var(--font-body);
  font-size: 0.85rem;
  line-height: 1.7;
  color: var(--gray-600);

  b, strong {
    color: var(--navy);
    font-weight: 700;
  }

  [style*="color: red"], [style*="color:red"] {
    color: var(--red) !important;
  }

  p {
    margin: 0 0 8px;
  }
`;

export const DetailCapacity = styled.div`
  margin-bottom: 8px;
`;

export const DetailActions = styled.div`
  flex-shrink: 0;
  display: flex;
  gap: 10px;
  padding: 16px 24px;
  background: var(--card-bg, #fff);
  border-top: 1.5px solid var(--border);
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.06);

  a, button {
    flex: 1;
    justify-content: center;
    padding: 12px 16px;
    font-size: 0.88rem;
  }

  @media (max-width: 600px) {
    padding: 12px 16px;
  }
`;

/* ═══════════════════════════════════════════
   Ad Card (Tekton)
   ═══════════════════════════════════════════ */

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const adProgressBar = keyframes`
  from { width: 100%; }
  to   { width: 0%; }
`;

export const AdCard = styled.aside`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 240px;
  background: var(--card-bg);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
  overflow: hidden;
  z-index: 900;
  animation: ${fadeInUp} var(--duration-normal) var(--ease-out);

  @media (max-width: 768px) {
    width: 180px;
    bottom: 16px;
    right: 16px;
  }

  @media (max-width: 400px) {
    width: 150px;
    bottom: 12px;
    right: 12px;
  }
`;

export const AdProgressBar = styled.div`
  height: 3px;
  background: var(--navy);
  animation: ${adProgressBar} 5s linear forwards;
  border-radius: 0 0 2px 2px;
`;

export const AdCardInTheGuard = styled.aside<{ offsetBottom?: boolean }>`
  position: fixed;
  bottom: ${({ offsetBottom }) => (offsetBottom ? "320px" : "20px")};
  right: 20px;
  width: 280px;
  background: #fff;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
  overflow: hidden;
  z-index: 901;
  animation: ${fadeInUp} var(--duration-normal) var(--ease-out);
  transition: bottom 0.3s ease;

  @media (max-width: 600px) {
    width: 65%;
    max-width: 220px;
    bottom: ${({ offsetBottom }) => (offsetBottom ? "280px" : "20px")};
  }
`;

export const AdClose = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(180, 0, 0, 0.55);
  border: none;
  border-radius: 50%;
  color: #fff;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  z-index: 2;
  transition: background var(--duration-fast);

  &:hover {
    background: rgba(180, 0, 0, 0.8);
  }
`;

export const AdBody = styled.div`
  padding: 8px 10px 10px;
  text-align: center;

  p {
    margin: 0 0 2px;
    font-family: var(--font-body);
    font-size: 0.82rem;
    font-weight: 600;
    color: var(--gray-800);
  }

  a {
    color: var(--navy);
    font-weight: 700;
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }

  small {
    font-size: 0.8rem;
    color: var(--gray-600);
  }
`;

/* ═══════════════════════════════════════════
   Loading & Empty
   ═══════════════════════════════════════════ */

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const LoadingIndicator = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-3);
  padding: var(--space-8) 0;
  color: var(--gray-500);
  font-family: var(--font-body);
  font-size: 0.9rem;

  &::before {
    content: "";
    width: 24px;
    height: 24px;
    border: 3px solid var(--gray-300);
    border-top-color: var(--navy);
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
  }
`;

export const EmptyState = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  text-align: center;
  color: var(--gray-400);

  h3 {
    font-family: var(--font-display);
    font-weight: 700;
    font-size: 1.3rem;
    color: var(--navy);
    margin: 0 0 var(--space-2);
  }

  p {
    font-family: var(--font-body);
    font-size: 0.9rem;
    color: var(--gray-500);
    margin: 0;
  }
`;

/* Re-export para compatibilidade com toggleDescription */
export const TournamentDescription = styled.div`
  font-family: var(--font-body);
  font-size: 0.82rem;
  line-height: 1.6;
  color: var(--gray-600);
  margin: 0;
  padding: 0;

  b, strong {
    color: var(--navy);
    font-weight: 700;
  }
`;

/* ═══════════════════════════════════════════
   OSS Button & Animations
   ═══════════════════════════════════════════ */

const ossFloatUp = keyframes`
  0%   { transform: translateY(0) scale(1);    opacity: 1; }
  60%  { opacity: 1; }
  100% { transform: translateY(-140px) scale(1.6); opacity: 0; }
`;

const ossPulse = keyframes`
  0%   { transform: scale(1); }
  40%  { transform: scale(1.3); }
  100% { transform: scale(1); }
`;

export const OssBubble = styled.button<{ $active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  background: rgba(26, 34, 82, 0.07);
  border: none;
  border-radius: 8px;
  padding: 4px 6px;
  cursor: pointer;
  z-index: 2;
  transition: transform 0.15s, background 0.2s;
  user-select: none;

  &:hover {
    background: rgba(26, 34, 82, 0.12);
    transform: scale(1.1);
  }

  &:active {
    ${css`animation: ${ossPulse} 0.28s ease-out;`}
    transform: scale(0.9);
  }
`;

export const OssLabel = styled.span`
  font-family: 'Showcard Gothic', var(--font-display), sans-serif;
  font-size: 0.85rem;
  font-weight: 400;
  color: #1A2252;
  line-height: 1;
`;

export const OssCount = styled.span`
  font-family: var(--font-body);
  font-size: 0.65rem;
  font-weight: 700;
  color: #1A2252;
  line-height: 1;
`;

export const OssParticle = styled.span<{ $x: number; $y: number; $delay: number; $color: "red" | "blue" }>`
  position: fixed;
  pointer-events: none;
  font-family: 'Showcard Gothic', var(--font-display), sans-serif;
  font-size: 1rem;
  font-weight: 400;
  color: ${({ $color }) => $color === "red" ? "#e4202c" : "#1A2252"};
  text-shadow: none;
  z-index: 9999;
  left: ${({ $x }) => $x}px;
  top: ${({ $y }) => $y}px;
  animation: ${ossFloatUp} 1.3s ease-out forwards;
  animation-delay: ${({ $delay }) => $delay}ms;
  opacity: 0;
  animation-fill-mode: both;
  white-space: nowrap;
`;

export const OssModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const OssModalBox = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  width: min(420px, 90vw);
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 8px 40px rgba(0,0,0,0.18);
`;

export const OssModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  h3 {
    font-family: var(--font-display);
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--navy, #1a2252);
    margin: 0;
  }
  button {
    background: none;
    border: none;
    font-size: 1.2rem;
    cursor: pointer;
    color: #666;
    line-height: 1;
    padding: 4px;
  }
`;

export const OssModalList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const OssModalItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #f5f7fa;
`;

export const OssModalAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--navy, #1a2252);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
  flex-shrink: 0;
`;

export const OssModalInfo = styled.div`
  flex: 1;
  min-width: 0;
  span {
    display: block;
    font-size: 0.85rem;
    color: #333;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  small {
    font-size: 0.72rem;
    color: #888;
  }
`;

/* ═══════════════════════════════════════════
   OSS Combo Bar & Float
   ═══════════════════════════════════════════ */

const comboBarPulse = keyframes`
  0%, 100% { box-shadow: 0 0 6px rgba(228, 32, 44, 0.6); }
  50% { box-shadow: 0 0 14px rgba(228, 32, 44, 0.9), 0 0 24px rgba(26, 34, 82, 0.4); }
`;

const comboBarShine = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

export const OssComboBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: linear-gradient(
    90deg,
    #e4202c 0%,
    #ff4d4d 25%,
    #e4202c 40%,
    #1A2252 70%,
    #4a6cf7 85%,
    #1A2252 100%
  );
  background-size: 200% 100%;
  animation: ${comboBarShine} 1s linear infinite, ${comboBarPulse} 0.6s ease-in-out infinite;
  border-radius: 0 3px 3px 0;
  transition: width 0.05s linear;
  z-index: 0;
  opacity: 0.35;

  &::after {
    content: '';
    position: absolute;
    right: 0;
    top: -3px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 0 8px #e4202c, 0 0 16px rgba(228, 32, 44, 0.5);
  }
`;

const explosionBurst = keyframes`
  0% {
    transform: translate(-50%, -50%) scale(0);
    opacity: 1;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.2);
    opacity: 0.8;
  }
  100% {
    transform: translate(-50%, -50%) scale(2);
    opacity: 0;
  }
`;

const explosionRing = keyframes`
  0% {
    transform: translate(-50%, -50%) scale(0);
    opacity: 1;
    border-width: 8px;
  }
  100% {
    transform: translate(-50%, -50%) scale(2.5);
    opacity: 0;
    border-width: 1px;
  }
`;

export const OssExplosion = styled.div`
  position: absolute;
  bottom: 0;
  left: 100%;
  width: 120px;
  height: 120px;
  z-index: 5;
  pointer-events: none;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(228,32,44,0.6) 0%, rgba(26,34,82,0.3) 50%, transparent 70%);
    animation: ${explosionBurst} 0.8s ease-out forwards;
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100px;
    height: 100px;
    border-radius: 50%;
    border: 4px solid #fff;
    background: transparent;
    animation: ${explosionRing} 0.8s ease-out forwards;
  }
`;

const comboFloatUp = keyframes`
  0% { opacity: 1; transform: translate(-50%, 0) scale(1); }
  50% { opacity: 1; transform: translate(-50%, -30px) scale(1.3); }
  100% { opacity: 0; transform: translate(-50%, -60px) scale(0.8); }
`;

export const OssComboFloat = styled.div<{ $x?: number; $y?: number }>`
  position: absolute;
  top: ${({ $y }) => $y ?? 40}%;
  left: ${({ $x }) => $x ?? 50}%;
  transform: translate(-50%, 0);
  font-family: 'Showcard Gothic', var(--font-display), sans-serif;
  font-size: 4rem;
  font-weight: 400;
  color: #fff;
  text-shadow:
    -2px -2px 0 rgba(0,0,0,0.7),
     2px -2px 0 rgba(0,0,0,0.7),
    -2px  2px 0 rgba(0,0,0,0.7),
     2px  2px 0 rgba(0,0,0,0.7),
     0 0 15px rgba(0,0,0,0.6),
     0 0 30px rgba(228, 32, 44, 0.4);
  z-index: 4;
  pointer-events: none;
  animation: ${comboFloatUp} 1.5s ease-out forwards;
`;

